import { useEffect, useRef, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import {
  Html5Qrcode,
  Html5QrcodeSupportedFormats,
  Html5QrcodeScannerState,
} from 'html5-qrcode'
import Button from '../components/ui/Button'
import { getHistory, removeFromHistory } from '../services/scanHistory'
import { toTitleCase, cleanProductName } from '../lib/text'
import { formatEur, formatLocalDate } from '../lib/format'
import { isMaskingActive } from '../services/maskMode'
import ChainLogo from '../components/ChainLogo'

const SCANNER_ID = 'barcode-reader'
const EAN_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
]

// Escalating hint thresholds (ms). Lowered so a struggling scan surfaces help
// fast — silence in a supermarket aisle reads as "frozen".
const HINT_LEVELS = [5000, 10000, 20000]

// A few recognizable chains shown as a coverage proof on the scanner placeholder.
// Missing logos are hidden via onError, so this list is safe to trim/extend.
const SCANNER_LOGOS = [
  'konzum',
  'lidl',
  'plodine',
  'spar',
  'kaufland',
  'tommy',
  'studenac',
  'eurospin',
  'dm',
  'ktc',
]

function isValidEan(value) {
  return /^\d+$/.test(value) && (value.length === 8 || value.length === 13)
}

/** Query the Permissions API for camera access state. */
async function queryCameraPermission() {
  if (!navigator.permissions?.query) return 'unknown'
  try {
    const result = await navigator.permissions.query({ name: 'camera' })
    return result.state // 'granted' | 'prompt' | 'denied'
  } catch {
    // Some browsers reject unsupported permission names
    return 'unknown'
  }
}

export default function ScannerPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [manualEan, setManualEan] = useState('')
  const [manualError, setManualError] = useState('')
  const [cameraError, setCameraError] = useState(false)
  const [permissionStatus, setPermissionStatus] = useState('unknown')
  const [scannerStarted, setScannerStarted] = useState(false)
  const [scanSuccess, setScanSuccess] = useState(false)
  const [scanHintLevel, setScanHintLevel] = useState(0)
  // Recently scanned products (local storage only). Read on mount, can update.
  const [history, setHistory] = useState(getHistory)
  const [confirmingEan, setConfirmingEan] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const scannerRef = useRef(null)
  const hintTimersRef = useRef([])
  const manualInputRef = useRef(null)
  const focusTimeoutRef = useRef(null)
  const permissionHandlerRef = useRef(null)

  // --- Permission detection (runs once on mount) ---
  useEffect(() => {
    let permissionStatusObj = null

    const detectPermission = async () => {
      const state = await queryCameraPermission()
      if (!permissionStatusObj || state !== 'unknown') {
        setPermissionStatus(state)
      }
      // If 'granted', the scanner start effect will handle it
      // If 'denied', show error and focus manual input
      if (state === 'denied') {
        setCameraError(
          true
        )
        // Focus manual input after a tick so the DOM is ready
        focusTimeoutRef.current = setTimeout(() => manualInputRef.current?.focus(), 100)
      }
    }

    // Try to subscribe to permission changes
    const subscribePermissionChange = async () => {
      if (!navigator.permissions?.query) return
      try {
        permissionStatusObj = await navigator.permissions.query({ name: 'camera' })
        setPermissionStatus(permissionStatusObj.state)

        // Handle initial denied state
        if (permissionStatusObj.state === 'denied') {
          setCameraError(
            true
          )
          focusTimeoutRef.current = setTimeout(() => manualInputRef.current?.focus(), 100)
        }

        const onChange = () => {
          const newState = permissionStatusObj.state
          setPermissionStatus(newState)
          if (newState === 'granted') {
            setCameraError(false)
          } else if (newState === 'denied') {
            setCameraError(
              true
            )
            focusTimeoutRef.current = setTimeout(() => manualInputRef.current?.focus(), 100)
          }
        }
        permissionHandlerRef.current = onChange
        permissionStatusObj.addEventListener('change', onChange)
      } catch {
        // Permission name not supported — fall back to initial query
        detectPermission()
      }
    }

    subscribePermissionChange()

    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
      }
      if (permissionStatusObj && permissionHandlerRef.current) {
        permissionStatusObj.removeEventListener('change', permissionHandlerRef.current)
      }
    }
  }, [])

  // --- Escalating hint timers ---
  const clearHintTimers = useCallback(() => {
    hintTimersRef.current.forEach(clearTimeout)
    hintTimersRef.current = []
    setScanHintLevel(0)
  }, [])

  const startHintTimers = useCallback(() => {
    clearHintTimers()
    HINT_LEVELS.forEach((ms, index) => {
      const timer = setTimeout(() => {
        setScanHintLevel(index + 1)
      }, ms)
      hintTimersRef.current.push(timer)
    })
  }, [clearHintTimers])

  // --- Start scanner (permission granted, or user clicked button) ---
  const startScanner = useCallback(() => {
    if (scannerRef.current) return // already initialised

    const scanner = new Html5Qrcode(SCANNER_ID, {
      formatsToSupport: EAN_FORMATS,
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      verbose: false,
    })
    scannerRef.current = scanner

    let cancelled = false
    let navigated = false

    const config = { fps: 10 }

    const onScan = (text) => {
      if (navigated) return
      navigated = true

      // Success flash: briefly turn the viewfinder border green
      setScanSuccess(true)
      clearHintTimers()

      setTimeout(() => {
        navigate(`/results/${text}`, { replace: true })
      }, 500)
    }

    const safeClear = () => {
      try {
        scanner.clear()
      } catch {
        /* nothing was rendered */
      }
    }

    const stop = () => {
      try {
        if (scanner.getState() === Html5QrcodeScannerState.SCANNING) {
          scanner.stop().then(safeClear).catch(safeClear)
          return
        }
      } catch {
        /* getState/stop may throw before start completes */
      }
      safeClear()
    }

    scanner
      .start({ facingMode: 'environment' }, config, onScan, undefined)
      .then(() => {
        if (cancelled) {
          stop()
        } else {
          setScannerStarted(true)
          startHintTimers()
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCameraError(
            true
          )
          // Reset ref so the scanner can be restarted (e.g. if permission is later granted)
          scannerRef.current = null
        }
      })

    // Store cleanup on the ref so unmount can call it
    scannerRef.current._cleanup = () => {
      cancelled = true
      stop()
      scannerRef.current = null
    }
  }, [navigate, startHintTimers, clearHintTimers])

  // --- Auto-start when permission becomes 'granted' ---
  useEffect(() => {
    if (permissionStatus === 'granted' && !scannerStarted && !cameraError) {
      startScanner()
    }
  }, [permissionStatus, scannerStarted, cameraError, startScanner])

  // --- Cleanup on unmount ---
  useEffect(() => {
    return () => {
      clearHintTimers()
      if (scannerRef.current?._cleanup) {
        scannerRef.current._cleanup()
      }
    }
  }, [clearHintTimers])

  // --- User clicks "Pokreni skener" ---
  const handleStartScanner = () => {
    setCameraError(false)
    startScanner()
  }

  // --- Scroll to manual input ---
  const scrollToManualInput = () => {
    manualInputRef.current?.focus()
    manualInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  function handleManualSubmit(e) {
    e.preventDefault()
    const value = manualEan.trim()
    if (!isValidEan(value)) {
      setManualError(t('scanner.manualError'))
      return
    }
    setManualError('')
    setIsSubmitting(true)
    setTimeout(() => {
      navigate(`/results/${value}`, { replace: true })
    }, 300)
  }

  // Determine which camera section to show
  const showPromptButton = (permissionStatus === 'prompt' || permissionStatus === 'unknown') && !scannerStarted && !cameraError
  const showDeniedError = permissionStatus === 'denied' && !scannerStarted

  return (
    <main id="main-content" className="max-w-md mx-auto px-4 py-10">
      <section
        aria-label={t('nav.scanner')}
        className="bg-surface rounded-card shadow-card overflow-hidden"
      >
        {/* Viewfinder container (positioned wrapper for the overlays). */}
        <div
          className={`relative mx-auto w-full overflow-hidden
            ${scanSuccess ? 'border-4 border-success' : ''}`}
        >
          {/* html5-qrcode renders its <video> into THIS node and mutates its
              children directly, so React must never render children inside it
              (that caused a "removeChild" crash on permission grant). It keeps
              its own intrinsic aspect-ratio box — that's what the library
              measures to size the video. All overlays below are siblings. */}
          <div
            id={SCANNER_ID}
            className="relative w-full aspect-[40/21] bg-black
              [&_video]:absolute [&_video]:inset-0 [&_video]:h-full [&_video]:w-full [&_video]:object-cover"
          />

          {/* --- Prompt state: grey placeholder with start button --- */}
          {showPromptButton && (
            <div className="absolute inset-0 bg-surface-2 flex flex-col items-center justify-center z-10 px-4 py-2">
              <div className="grid grid-cols-5 gap-1.5 mb-4">
                {SCANNER_LOGOS.map((slug, li) => (
                  <ChainLogo
                    key={slug}
                    chain={slug}
                    index={li}
                    masked={isMaskingActive()}
                    size="h-[18px] w-[18px] text-[10px]"
                    shape="rounded"
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleStartScanner}
                className="flex items-center justify-center gap-2 w-full max-w-[220px] h-10 bg-primary hover:bg-primary-hover active:bg-primary-active
                  text-on-primary text-sm font-semibold rounded-card transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                {t('scanner.start')}
              </button>
              <p className="text-xs text-muted text-center max-w-[260px] mt-2">
                {t('scanner.cameraReassurance')}
              </p>
            </div>
          )}

          {/* --- Denied state: camera-unavailable guidance --- */}
          {showDeniedError && (
            <div className="absolute inset-0 bg-surface-2 z-10 p-4 overflow-y-auto flex flex-col items-center justify-center">
              <p className="text-base font-semibold text-fg">{t('scanner.cameraUnavailable')}</p>
              <p className="text-sm text-muted text-center mt-2 max-w-[260px]">
                {t('scanner.cameraGuidance')}
              </p>
              <details className="mt-3 text-sm text-muted cursor-pointer">
                <summary className="font-medium text-primary-text hover:underline">{t('scanner.cameraHelpToggle')}</summary>
                <ul className="mt-2 space-y-1 list-disc pl-5 text-muted">
                  <li>{t('scanner.cameraHelpChrome')}</li>
                  <li>{t('scanner.cameraHelpSafari')}</li>
                </ul>
              </details>
            </div>
          )}

          {/* --- Viewfinder overlays (shown when scanner is active) --- */}
          {scannerStarted && (
            <>
              {/* Instruction strip at the bottom */}
              <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-sm text-center py-1 pointer-events-none z-20">
                {t('scanner.aim')}
              </div>

              {/* Scan-line animation */}
              <div
                data-testid="scan-line"
                className="absolute left-2 right-2 h-0.5 bg-white/60 pointer-events-none z-20 scan-line"
              />
            </>
          )}

          {/* Scan success overlay */}
          {scanSuccess && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-30">
              <p className="text-white text-lg font-semibold">{t('scanner.searching')}</p>
            </div>
          )}
        </div>

        {/* Camera error message (outside viewfinder, for denied/general errors) */}
        {cameraError && !showPromptButton && !showDeniedError && (
          <p role="alert" className="px-4 py-3 text-base text-warn-fg bg-warn-surface">
            {t('scanner.cameraErrorFull')}
          </p>
        )}

        {/* --- Escalating scan hints (below viewfinder, only when scanner is running) --- */}
        {scannerStarted && scanHintLevel >= 1 && (
          <div className="px-4 pb-2">
            {scanHintLevel < 2 && (
              <>
                <p className="text-base text-muted text-center mt-2">
                  {t('scanner.hintHold')}
                </p>
                <button
                  type="button"
                  onClick={scrollToManualInput}
                  className="block mx-auto py-3 text-base text-primary-text underline mt-1"
                >
                  {t('scanner.hintManualLink')}
                </button>
              </>
            )}
            {scanHintLevel >= 2 && (
              <div className="mt-2">
                <p className="text-base text-warn-fg text-center">
                  {t('scanner.hintLong')}
                </p>
                <button
                  type="button"
                  onClick={scrollToManualInput}
                  className="mt-3 w-full bg-primary hover:bg-primary-hover active:bg-primary-active text-on-primary rounded-btn px-3 py-3 text-base font-semibold transition-colors"
                >
                  {t('scanner.manualEntryButton')}
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      <header className="mt-4 text-center">
        {/* Tri-color brand wordmark: navy "Provjeri" + green "Cijene" + purple ".hr".
            The string is identical in every locale, so it's rendered literally
            rather than duplicated across the locale files. Splitting ".hr" into its
            own span keeps the concatenated accessible name "ProvjeriCijene.hr"
            (asserted by the e2e tests). */}
        <h1 className="text-3xl font-extrabold text-fg">
          Provjeri<span className="text-primary">Cijene</span><span className="text-accent">.hr</span>
        </h1>
        {/* Benefit-led value headline: "Skeniraj barkod. / Pronađi najnižu cijenu."
            with the hook phrase in green (green = cheapest price). The marketing
            proof points (coverage pill, "besplatno" trust line) live only on the
            OG/social image and SEO meta — on the workflow screen they'd compete
            with the scanner controls, so they're deliberately omitted here. */}
        <p className="mt-1.5 text-[1.375rem] font-extrabold leading-tight text-fg">
          {t('hero.headlineScan')}<br />
          {t('hero.headlineFind')}{' '}
          <span className="text-primary">{t('hero.headlineHook')}</span>
        </p>
      </header>

      <form onSubmit={handleManualSubmit} className="mt-6 bg-surface rounded-card shadow-card p-4">
        <label htmlFor="manual-ean" className="block text-base font-medium text-muted">
          {t('scanner.manualLabel')}
        </label>
        <div className="mt-2 flex gap-2">
          <input
            ref={manualInputRef}
            id="manual-ean"
            name="manual-ean"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder={t('scanner.manualPlaceholder')}
            value={manualEan}
            onChange={(e) => setManualEan(e.target.value)}
            className="min-w-0 flex-1 rounded-btn border border-line px-3 py-3 text-lg focus:border-primary focus:ring-primary"
          />
          <Button type="submit" size="lg" isLoading={isSubmitting} className="shrink-0">{t('scanner.search')}</Button>
        </div>
        {manualError && (
          <p role="alert" className="mt-2 text-sm text-danger">
            {manualError}
          </p>
        )}
      </form>

      {history.length > 0 && (
        <section aria-label={t('scanner.recentTitle')} className="mt-6">
          <h2 className="px-1 text-base font-semibold text-muted">{t('scanner.recentTitle')}</h2>
          <ul className="mt-2 space-y-2">
            {history.map((item) => {
              const displayName = cleanProductName(item.name) || item.ean
              const detail = toTitleCase(item.brand)
              return (
                <li key={item.ean}>
                  {confirmingEan === item.ean ? (
                    <div className="rounded-card border border-line bg-surface p-3">
                      <p className="text-base text-muted">
                        <Trans
                          i18nKey="scanner.removeConfirm"
                          values={{ name: displayName }}
                          components={{ b: <span className="font-medium" /> }}
                        />
                      </p>
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => setConfirmingEan(null)}
                          className="flex-1 rounded-btn bg-surface-2 px-3 py-3 text-sm font-medium text-muted hover:bg-surface-2-hover transition-colors"
                        >
                          {t('scanner.no')}
                        </button>
                        <button
                          onClick={() => {
                            setHistory(removeFromHistory(item.ean))
                            setConfirmingEan(null)
                          }}
                          className="flex-1 rounded-btn bg-danger px-3 py-3 text-sm font-medium text-on-danger hover:bg-danger-hover transition-colors"
                        >
                          {t('scanner.yesRemove')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start rounded-card border border-line bg-surface p-4 hover:border-primary">
                      <Link
                        to={`/results/${item.ean}`}
                        className="flex-1 min-w-0"
                      >
                        <p className="font-medium text-fg">{displayName}</p>
                        {detail && <p className="text-base text-muted">{detail}</p>}
                        {item.cheapestPrice != null && (
                          <p className="mt-1 text-base text-muted">
                            {formatEur(item.cheapestPrice)} · {formatLocalDate(item.scannedAt)}
                          </p>
                        )}
                      </Link>
                      <button
                        onClick={() => setConfirmingEan(item.ean)}
                        className="shrink-0 ml-2 p-1 rounded hover:bg-surface-2 transition-colors"
                        aria-label={t('scanner.removeAria', { name: displayName })}
                      >
                        <svg className="h-5 w-5 text-faint hover:text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </main>
  )
}
